// Copyright (c) 2026 FlexiProof. All rights reserved.
//
// This source code file is licensed under the terms of the MIT license, a copy
// of which may be found in the LICENSE.md file in the root of this repository.
//
// For a template copy of the license see one of the following 3rd party sites:
//      - <https://opensource.org/licenses/MIT>
//      - <https://choosealicense.com/licenses/mit>
//      - <https://spdx.org/licenses/MIT>

/**
 * @file Global state object module.
 * @author James Reid
 */

// @ts-check

// @@imports-types
/* eslint-disable no-unused-vars -- Types only used in comments. */
import { State } from "../types/index.js"
import { Stock } from "../../../package/stock/Stock.js"
import { Application } from "pixi.js"
import { Deck, EpdmCalculator, presets, Vector2D } from "../../../package/index.js"
import { ApexRenderer, DeckRenderer } from "../components/index.js"
/* eslint-enable no-unused-vars -- Close disable-enable pair. */

// @@body
// const defaultState = /** @type {State} */ ({
const defaultState = ({
    quote: {
        uuid: null,
        merchant: null,
        stock: /** @type {Stock?} */ (null),
        calculator: /** @type {EpdmCalculator?} */ (null)
    },
    apex: {
        renderer: /** @type {ApexRenderer?} */ (null)
    },
    deck: {
        id: /** @type {String?} */ (null),
        type: "flat",
        default: presets.rectangular,
        client: /** @type {Deck?} */ (null),
        spritesheet: null,
        renderer: /** @type {DeckRenderer?} */ (null)
    },
    pageOptions: {
        token: /** @type {?string} */ (null),
        role: "client",
        lifecycle: "new",
        hideBasket: false,
        hidePrices: false,
        showSurcharges: false,
        priceList: undefined,
        clientType: undefined
    },

    epdmCalculator: null,
    callbackUrl: null,
    stock: null,
    deckOption: null,
    spritesheet: null,
    deckEditor: {
        deck: null,
        app: new Application(),
        container: null,
        anchor: new Vector2D(),
        anchorOffset: new Vector2D(0, 0),
        scale: NaN,
        scaleFactor: 1,
        get width() { return this.app.renderer?.width || NaN },
        get height() { return this.app.renderer?.height || NaN },
        get pixelSize() { return 10 / this.scale },
        get targetAnchor() {
            // 
            if (!this.deck) { return new Vector2D() }

            // 
            const { width, height } = this.deck.boundingBox
            const scale = this.targetScale

            const anchorX = (this.width - width * scale) / 2
            const anchorY = (this.height - height * scale) / 2
            const anchor = new Vector2D(anchorX, anchorY)
                .add(this.anchorOffset)

            return anchor
        },
        get targetScale() {
            // 
            if (!this.deck) { return NaN }

            // 
            const { width, height } = this.deck.boundingBox
            const minScale = Math.min(this.width / width, this.height / height)
            const appScale = minScale * 0.7 * this.scaleFactor

            return appScale
        },
        redraw: () => {},
        render: () => {},
        rerender: () => {},
        selectedEdge: null,
        renderEdgeTypes: false,
        metric: true,
        renderOptions: {
            units: "metric",
            fillColor: "",
            edgeColor: "",
            edgeColorSelected: "",
            edgeWidth: "",
            edgeWidthSelected: ""
        }
    }
})

// @@exports
export { defaultState }
